/**
 * Course and lead recruitment validation utilities, domain constants, and GDPR Article 9 guardrails.
 */

export const COURSE_STATUSES = {
    OPEN: "open",
    FEW_SEATS: "few_seats",
    FULL: "full",
    COMPLETED: "completed",
};

export const COURSE_STATUS_LABELS = {
    open: "Ledige plasser",
    few_seats: "Få plasser igjen",
    full: "Fulltegnet",
    completed: "Gjennomført",
};

export const COURSE_STATUS_COLORS = {
    open: "success",
    few_seats: "warning",
    full: "error",
    completed: "default",
};

/**
 * Sensitive medical/psychiatric keywords prohibited under GDPR Article 9 privacy-by-design.
 */
export const SENSITIVE_HEALTH_KEYWORDS = [
    "diagnose",
    "sykdom",
    "resept",
    "medisin",
    "antidepressiva",
    "angstdempende",
    "psykiatri",
    "legeerklæring",
    "epikrise",
    "schizofreni",
    "bipolar",
    "selvmord",
    "selvskading",
    "pasientjournal",
    "psykofarmaka",
];

/**
 * Checks whether text contains prohibited sensitive health terminology.
 * @param {string} text 
 * @returns {boolean}
 */
export const containsSensitiveHealthData = (text) => {
    if (!text || typeof text !== "string") return false;
    const lower = text.toLowerCase();
    return SENSITIVE_HEALTH_KEYWORDS.some((kw) => {
        const regex = new RegExp(`\\b${kw}\\b`, "i");
        return regex.test(lower);
    });
};

/**
 * Validates an upcoming course cohort object.
 * @param {object} course 
 * @returns {{isValid: boolean, errors: Record<string, string>}}
 */
export const validateUpcomingCourse = (course) => {
    const errors = {};
    if (!course || typeof course !== "object") {
        return { isValid: false, errors: { general: "Ugyldig kursdata." } };
    }

    const title = (course.title || "").trim();
    if (!title) {
        errors.title = "Kurstittel er påkrevd.";
    } else if (title.length < 3) {
        errors.title = "Kurstittelen må bestå av minst 3 tegn.";
    } else if (title.length > 120) {
        errors.title = "Kurstittelen kan maksimalt være 120 tegn.";
    }

    const startDate = (course.startDate || "").trim();
    if (!startDate) {
        errors.startDate = "Oppstartsdato er påkrevd.";
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
        errors.startDate = "Ugyldig datoformat (bruk ÅÅÅÅ-MM-DD).";
    }

    const location = (course.location || "").trim();
    if (!location) {
        errors.location = "Oppmøtested/lokasjon er påkrevd.";
    } else if (location.length > 150) {
        errors.location = "Lokasjon kan maksimalt være 150 tegn.";
    }

    const validStatuses = Object.values(COURSE_STATUSES);
    if (!course.status || !validStatuses.includes(course.status)) {
        errors.status = "Ugyldig status for kurset.";
    }

    const description = (course.description || "").trim();
    if (description.length > 1000) {
        errors.description = "Beskrivelsen kan maksimalt være 1000 tegn.";
    }

    const regUrl = (course.registrationUrl || "").trim();
    if (regUrl) {
        if (!/^https?:\/\/[^\s$.?#].[^\s]*$/i.test(regUrl)) {
            errors.registrationUrl = "Påmeldingslenke må være en gyldig nettadresse (f.eks. https://...).";
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Validates a lead inquiry submitted by a prospective participant.
 * @param {object} lead 
 * @returns {{isValid: boolean, errors: Record<string, string>}}
 */
export const validateCourseLead = (lead) => {
    const errors = {};
    if (!lead || typeof lead !== "object") {
        return { isValid: false, errors: { general: "Ugyldig henvendelsesdata." } };
    }

    const name = (lead.name || "").trim();
    if (!name) {
        errors.name = "Fullt navn er påkrevd.";
    } else if (name.length < 2) {
        errors.name = "Navnet må være minst 2 tegn.";
    } else if (name.length > 100) {
        errors.name = "Navnet kan ikke overstige 100 tegn.";
    }

    const email = (lead.email || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
        errors.email = "E-postadresse er påkrevd.";
    } else if (!emailRegex.test(email)) {
        errors.email = "Oppgi en gyldig e-postadresse.";
    }

    const phone = (lead.phone || "").trim();
    if (phone) {
        const cleaned = phone.replace(/[\s\-+()]/g, "");
        if (!/^\d{5,15}$/.test(cleaned)) {
            errors.phone = "Oppgi et gyldig telefonnummer (minst 5 siffer).";
        }
    }

    const message = (lead.message || "").trim();
    if (message.length > 500) {
        errors.message = "Meldingen kan maksimalt være 500 tegn.";
    } else if (containsSensitiveHealthData(message)) {
        errors.message = "Av hensyn til ditt personvern ber vi om at du ikke oppgir helseopplysninger eller diagnoser.";
    }

    if (!lead.hasConsent) {
        errors.hasConsent = "Du må samtykke til at kursholderen kan kontakte deg om kurset.";
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Checks if an instructor has active, planned courses that are enrolling.
 * @param {object} instructor 
 * @returns {boolean}
 */
export const hasActiveUpcomingCourses = (instructor) => {
    if (!instructor || !Array.isArray(instructor.upcomingCourses)) {
        return false;
    }
    return instructor.upcomingCourses.some(
        (c) => c && (c.status === COURSE_STATUSES.OPEN || c.status === COURSE_STATUSES.FEW_SEATS)
    );
};

/**
 * Checks whether a single course cohort is currently active (not completed).
 * @param {object} course
 * @returns {boolean}
 */
export const isCourseActive = (course) => {
    return Boolean(course && course.status !== COURSE_STATUSES.COMPLETED);
};

/**
 * Extracts and sorts active upcoming courses for an instructor.
 * @param {object} instructor 
 * @returns {Array<object>}
 */
export const getActiveUpcomingCourses = (instructor) => {
    if (!instructor || !Array.isArray(instructor.upcomingCourses)) {
        return [];
    }
    return instructor.upcomingCourses
        .filter(isCourseActive)
        .sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
};
